import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Switch,
  Link,
  RadioGroup,
  Radio,
  Dropdown,
  DropdownOverlay,
  SelectInput,
  ActionList,
  ActionListItem,
  Divider,
  ArrowRightIcon,
  Badge,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { isTaxExempt } from 'components/WizardViews/SalaryComponents/utils';
import { flexibleBenefitsTaxSections } from 'components/WizardViews/SalaryComponents/constants';
import { FlexibleBenefitFormData } from './types';

export const TaxDetails = ({ isEditFlow = false }: { isEditFlow?: boolean }) => {
  const { formData, setFormData, errors } = useFormWithProvider<FlexibleBenefitFormData>();
  const showTaxabilitySection = formData?.showInGrossSalary || false;
  const shouldForceHoldUnclaimedAmount = !formData?.showInGrossSalary;
  const isArrearComingSoon = true;

  const handleTaxBehaviourChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      tax_behaviour: {
        ...formData.tax_behaviour,
        [field]: value,
      },
    });
  };

  const handleArrearBehaviourChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      arrear_behaviour: {
        ...formData.arrear_behaviour,
        [field]: value,
      },
    });
  };

  const handleUnclaimedAmountChange = (field: string, value: string | boolean) => {
    if (shouldForceHoldUnclaimedAmount && value === 'false') {
      return;
    }

    setFormData({
      ...formData,
      hold_settings: {
        ...formData.hold_settings,
        [field]: value,
      },
    });
  };

  const handleProrationBehaviourChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      proration_behaviour: {
        ...formData.proration_behaviour,
        [field]: value,
      },
      tax_behaviour: {
        ...formData.tax_behaviour,
        tax_deduction_behaviour: value ? 'pro_rated' : 'instant',
      },
    });
  };
  const showExemptionSection = useMemo(
    () =>
      isTaxExempt(
        formData?.tax_behaviour?.tax_exempt_in_old_regime,
        formData?.tax_behaviour?.tax_exempt_in_new_regime,
      ),
    [
      formData?.tax_behaviour?.tax_exempt_in_old_regime,
      formData?.tax_behaviour?.tax_exempt_in_new_regime,
    ],
  );

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Tax & others details
        </Heading>
      </Box>

      {showTaxabilitySection && (
        <Box display="flex" flexDirection="row" gap="spacing.5">
          <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
            <Heading size="medium" weight="semibold" color="surface.text.gray.subtle">
              Taxability
            </Heading>
            <Link
              href="https://razorpay.com/docs/payroll/taxation"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              icon={ArrowRightIcon}
              iconPosition="right">
              Learn more
            </Link>
          </Box>

          <Box display="flex" flexDirection="column" gap="spacing.5" width={'50%'}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="spacing.4">
              <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                Tax exempted in Old Regime
              </Text>
              <Switch
                accessibilityLabel="Toggle tax exemption for Old Regime"
                isChecked={formData?.tax_behaviour?.tax_exempt_in_old_regime || false}
                onChange={({ isChecked }) =>
                  handleTaxBehaviourChange('tax_exempt_in_old_regime', isChecked)
                }
              />
            </Box>

            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="spacing.4">
              <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                Tax exempted in New Regime
              </Text>
              <Switch
                accessibilityLabel="Toggle tax exemption for New Regime"
                isChecked={formData?.tax_behaviour?.tax_exempt_in_new_regime || false}
                onChange={({ isChecked }) =>
                  handleTaxBehaviourChange('tax_exempt_in_new_regime', isChecked)
                }
              />
            </Box>

            {showExemptionSection && (
              <Box maxWidth="400px">
                <Dropdown>
                  <SelectInput
                    label="Exemption under Section"
                    placeholder="Select Section"
                    name="taxExemptionSection"
                    value={formData.tax_behaviour?.tax_exemption_section}
                    onChange={({ values }) =>
                      handleTaxBehaviourChange('tax_exemption_section', values[0])
                    }
                    validationState={errors.tax_exemption_section ? 'error' : 'none'}
                    errorText={errors.tax_exemption_section}
                  />
                  <DropdownOverlay>
                    <ActionList>
                      {flexibleBenefitsTaxSections.map((section) => (
                        <ActionListItem
                          key={section.key}
                          title={section.label}
                          description={section.description}
                          value={section.key}
                        />
                      ))}
                    </ActionList>
                  </DropdownOverlay>
                </Dropdown>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Divider />

      <>
        <Box display="flex" flexDirection="row" gap="spacing.5">
          <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
            <Heading size="medium" weight="semibold" color="surface.text.gray.subtle">
              Proration & Arrears
            </Heading>
            <Link
              href="https://razorpay.com/docs/payroll/proration-arrears"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              icon={ArrowRightIcon}
              iconPosition="right">
              Learn more
            </Link>
          </Box>

          <Box display="flex" flexDirection="column" gap="spacing.6" width={'50%'}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="spacing.4">
              <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                  Component will be prorated
                </Text>
                <Text size="small" weight="regular" color="surface.text.gray.muted">
                  Amount will be adjusted based on the employee's working period within the pay
                  cycle.
                </Text>
              </Box>
              <Switch
                accessibilityLabel="Toggle component proration"
                isChecked={formData?.proration_behaviour?.support_proration || false}
                onChange={({ isChecked }) =>
                  handleProrationBehaviourChange('support_proration', isChecked)
                }
              />
            </Box>
            {formData.showInGrossSalary ? (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap="spacing.4">
                <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                  <Box display="flex" alignItems="center" gap="spacing.3">
                    <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                      Calculate arrears
                    </Text>
                    {isArrearComingSoon && <Badge color="notice">Coming soon</Badge>}
                  </Box>
                  <Text size="small" weight="regular" color="surface.text.gray.muted">
                    Adjust salary for past periods when there were changes, like salary revisions or
                    missed payments.
                  </Text>
                </Box>
                <Switch
                  accessibilityLabel="Toggle calculate arrears"
                  isChecked={formData?.arrear_behaviour?.consider_in_arrear || false}
                  isDisabled={isArrearComingSoon || isEditFlow}
                  onChange={({ isChecked }) =>
                    handleArrearBehaviourChange('consider_in_arrear', isChecked)
                  }
                />
              </Box>
            ) : null}
          </Box>
        </Box>
        <Divider />
      </>

      {!formData?.nonCashBenefit && (
        <>
          <Box display="flex" flexDirection="row" gap="spacing.5">
            <Box display="flex" flexDirection="column" gap="spacing.2" width={'50%'}>
              <Heading size="medium" weight="semibold" color="surface.text.gray.subtle">
                Unclaimed amount
              </Heading>
            </Box>

            <Box maxWidth="600px">
              <RadioGroup
                label=""
                name="holdUnclaimedAmount"
                value={
                  shouldForceHoldUnclaimedAmount ? 'true' : formData?.hold_settings?.hold_unclaimed_amount
                }
                onChange={({ value }) =>
                  handleUnclaimedAmountChange('hold_unclaimed_amount', value)
                } isDisabled={isEditFlow}>
                <Radio
                  value="true"
                  helpText="Hold unclaimed amount till March month of the financial year">
                  Hold amount
                </Radio>
                <Radio
                  value="false"
                  isDisabled={shouldForceHoldUnclaimedAmount || isEditFlow}
                  helpText="Release unclaimed amount every payroll month as taxable income">
                  Release amount
                </Radio>
              </RadioGroup>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
